import s from './ChatView.module.css';
import {
    ClipIcon,
    DeleteChatIcon,
    DotsIcon,
    MaxIcon,
    SoundOffIcon,
    TelegramIcon,
    VKIcon,
} from "@/utils/icons";
import {useEffect, useRef, useState} from "react";
import classNames from "classnames";
import {useOutside} from "@/hooks";
import {useClickAway} from 'react-use';
import UserImage from "@common/Chat/components/UserImage/UserImage";
import {useSelector} from "react-redux";
import {selectProfile} from "@store/profile.reducer";
import {chatApi} from "@/api";
import {useAddAlert} from "@/hooks/useAddAlert";
import {getMessageText} from '@/helpers/messageText';
import { getSupportTicketManagerName, getSupportTicketText } from '../../helpers/supportTickets';
import CompactSelect from '../CompactSelect/CompactSelect';
import TooltipWrapper from '@UI/New/TooltipWrapper/TooltipWrapper';
import { getNotificationPlainText } from '../../helpers/mentions';
import { TeamOutlined } from '@ant-design/icons';
import {
    getLessonChatDisplayTitle,
    isGeneralLessonChat,
} from '@/helpers/lessonChats';

const isChatMuted = (value) => value === true || value === 'true' || Number(value) === 1;
const EXTERNAL_CHANNEL_LABELS = {
    telegram: 'TG',
    max: 'MAX',
    vk: 'VK',
};
const EXTERNAL_CHANNEL_ICONS = {
    telegram: TelegramIcon,
    max: MaxIcon,
    vk: VKIcon,
};

const getLastMessagePreview = (message) =>
    getNotificationPlainText(getMessageText(message)).replace(/\s+/g, ' ').trim();

const ChatView = ({
    chat,
    setPage,
    setHeader,
    setIsVisiblePopup,
    setType,
    setChatId,
    unReadChats,
    isTechSupport,
    supportStatusOptions = [],
    supportManagerOptions = [],
    onChangeSupportStatus,
    onAssignSupportTicketManager,
    onReadStateChange,
}) => {
    const addAlert = useAddAlert()

    const [isActive, setIsActive] = useState(false);
    const [muted, setMuted] = useState(isChatMuted(chat?.muted))
    const [externalMuted, setExternalMuted] = useState(chat?.externalNotifications?.muted ?? {})
    const menuRef = useRef(null)
    const [ref, isShow, setIsShow] = useOutside();
    const profile = useSelector(selectProfile);
    const interlocutorStatus = chat?.type === 'default' ? chat?.members[0]?.statusLabel : null;
    const getPhoto = () => {
        if (chat?.type === 'group' || chat?.type === 'notification') {
            return chat.avatar;
        } 
        else if(chat?.type === 'support'){
            return chat?.members?.find((el)=>el.role == 1)?.avatar
        }
        else {
            const img = chat?.members?.[0]
            return img?.avatar;
        }
    }

    const photo = getPhoto()
    const lessonChatKind = chat?.lesson_chat_kind ?? chat?.lessonChatKind;
    const isLessonChat = chat?.scope === 'lesson' || Boolean(lessonChatKind);
    const isGeneralChat = isLessonChat && isGeneralLessonChat(chat);
    const lessonChatTitle = getLessonChatDisplayTitle(chat);
    const chatTitle = isLessonChat
        ? lessonChatTitle
        : chat?.type === 'default'
            ? chat?.members?.[0]?.name
            : chat?.name;
    const lastMessagePreview = getLastMessagePreview(chat?.lastMessage?.message);

    const isFixing = false;
    const openMenu = (e) => {
        e.stopPropagation()
        setIsActive(!isActive)
    }

    const handleDelete = (e) => {
        e.stopPropagation()
        setChatId(chat.id)
        setType('remove')
        setHeader(isLessonChat
            ? 'Удалить чат урока? Переписка станет недоступна. Пока чаты в уроке включены, на его месте появится новый пустой чат.'
            : 'Вы хотите удалить данный чат?')
        setIsVisiblePopup(true)
    }


    const handleClip = (e) => {
        e.stopPropagation()
        setType('clip')
        setHeader('Вы хотите закрепить данный чат?')
        setChatId(chat.id)
        setIsVisiblePopup(true)
    }

    const handleUnfix = (e) => {
        e.stopPropagation()
        setType('unfix')
        setHeader('Вы хотите открепить данный чат?')
        setChatId(chat.id)
        setIsVisiblePopup(true)
    }

    const handleMute = (e) => {
        e.stopPropagation()

        chatApi.mute(chat.id)
          .then(() => {
              setMuted(true)
              setIsActive(false)

              addAlert({
                  text: 'Уведомления были отключены'
              })
          })
          .catch(() => setMuted(false))
    }

    const handleUnmute = (e) => {
        e.stopPropagation()

        chatApi.unmute(chat.id)
          .then(() => {
              setMuted(false)
              setIsActive(false)

              addAlert({
                  text: 'Уведомления были включены'
              })
          })
          .catch(() => setMuted(true))
    }

    const handleExternalMute = (channel, nextMuted) => (e) => {
        e.stopPropagation()

        chatApi.setExternalNotificationMuted(chat.id, channel, nextMuted)
          .then(() => {
              setExternalMuted((current) => ({...current, [channel]: nextMuted}))
              setIsActive(false)
              addAlert({
                  text: `Уведомления ${EXTERNAL_CHANNEL_LABELS[channel]} ${nextMuted ? 'отключены' : 'включены'}`
              })
          })
          .catch(() => addAlert({
              type: 'error',
              text: 'Не удалось изменить настройки уведомлений'
          }))
    }

    useClickAway(menuRef, () => {
        setIsActive(false);
    });

    useEffect(() => {
        setMuted(isChatMuted(chat?.muted))
    }, [chat?.muted])

    useEffect(() => {
        setExternalMuted(chat?.externalNotifications?.muted ?? {})
    }, [chat?.externalNotifications?.muted])

    const externalChannels = chat?.externalNotifications?.channels ?? []

    const isUnreadMessage = unReadChats.find(el => el.chatId == chat.id);

    const handleChangeSupportStatus = async (value) => {
        if (!onChangeSupportStatus) return

        await onChangeSupportStatus(chat?.id, value)
    }

    const handleAssignSupportTicketManager = async (value) => {
        if (!onAssignSupportTicketManager) return

        await onAssignSupportTicketManager(chat?.id, value)
    }

    const supportTicket = chat?.supportTicket
    const supportTicketManagerId = supportTicket?.managerId ?? supportTicket?.manager?.id
    
    const read = async (e) => {
        e.stopPropagation()

        try {
            if (isUnreadMessage) {
                await chatApi.readAllChatMessages(chat.id)
            } else {
                await chatApi.read(chat.id, {read: false})
            }

            setIsActive(false)
            await onReadStateChange?.()
        } catch {
            addAlert({type: 'error', title: 'Не удалось изменить статус прочтения чата'})
        }
    }

    return (
        <>
            {/* Порядок задаёт список: бэкенд сортирует закреплённые → непрочитанные → активность,
                а realtime-обновление пересортировывает массив. Собственный CSS order здесь
                перекрывал обе сортировки. */}
            <div
                className={classNames(s.chat, chat?.type === 'support' && isTechSupport && s.supportChat)}
                onClick={() => setPage('inChat', chat.id)}
            >
                {chat.isFixed && <div className={s.clip}>
                    <ClipIcon width={'24'} heeight={'24'}/>
                </div>}
                {isGeneralChat ? (
                    <TooltipWrapper placement='right' title='Общий чат'>
                        <span className={s.generalLessonChatAvatar}>
                            <TeamOutlined />
                        </span>
                    </TooltipWrapper>
                ) : (
                    <TooltipWrapper placement='right' title={chatTitle || 'Чат'}>
                        <span className={s.avatarTooltipTarget}>
                            <UserImage path={photo} previewDisabled={false}/>
                        </span>
                    </TooltipWrapper>
                )}
                {(chat?.type === 'support' || chat?.type === 'group' || chat?.type === 'notification') && (
                    <div className={s.chatBody}>
                        <div className={s.title}>
                            {isLessonChat ? lessonChatTitle : chat.name}
                        </div>
                        {chat?.type === 'support' && isTechSupport && (
                            <div
                                className={s.supportTicket}
                                onClick={(event) => event.stopPropagation()}
                                onMouseDown={(event) => event.stopPropagation()}
                            >
                                <div className={s.supportTicketMeta}>
                                    <span className={s.supportTicketText}>
                                        {getSupportTicketText(supportTicket)}
                                    </span>
                                    <span className={s.supportTicketText}>
                                        Менеджер: {getSupportTicketManagerName(supportTicket)}
                                    </span>
                                </div>
                                <div className={s.supportControlsRow}>
                                    {!!supportManagerOptions.length && !supportTicket?.isClosed && (
                                        <TooltipWrapper placement='left' title='Менеджер'>
                                            <div>
                                                <CompactSelect
                                                    value={supportTicketManagerId}
                                                    options={supportManagerOptions}
                                                    placeholder="Выберите менеджера"
                                                    onChange={handleAssignSupportTicketManager}
                                                    className={s.supportManagerSelect}
                                                />
                                            </div>
                                        </TooltipWrapper>
                                    )}
                                    <TooltipWrapper placement='right' title='Статус'>
                                        <div>
                                            <CompactSelect
                                                value={chat?.supportStatus?.id ?? 0}
                                                options={supportStatusOptions}
                                                onChange={handleChangeSupportStatus}
                                                className={s.supportStatusSelect}
                                            />
                                        </div>
                                    </TooltipWrapper>
                                    
                                </div>
                            </div>
                        )}
                        {!!lastMessagePreview && (
                            <div className={s.infoMember}>
                                <UserImage path={chat.lastMessage?.authorAvatar} customClass={s.photoMember} width={32}
                                           height={32} previewDisabled={false}/>
                                <div className={s.message}>{lastMessagePreview}</div>
                            </div>
                        )}
                    </div>
                )}
                {(chat?.type === 'default') && <div className={s.chatBody}>
                    <div className={s.title}>
                        {chat?.members[0]?.name}
                        {!!interlocutorStatus?.trim() && <span style={{color: 'var(--color-text-soft)'}}> ({interlocutorStatus})</span>}
                    </div>
                    {!!lastMessagePreview && <div className={s.message}>{lastMessagePreview}</div>}
                </div>}
                <div className={s.info}>
                    <div className={s.infoFooter}>
                        {muted && (
                            <TooltipWrapper placement='left' title='Уведомления выключены'>
                                <span className={s.mutedIcon}><SoundOffIcon /></span>
                            </TooltipWrapper>
                        )}
                        {isUnreadMessage && <div className={s.unread}>
                            {isUnreadMessage?.manualFlag ? '' : isUnreadMessage.count}
                        </div>}
                        {isFixing && <span className={s.clip}><ClipIcon/></span>}
                    </div>
                </div>
                <div className={s.dots} onClick={(e) => openMenu(e)}><DotsIcon/></div>
                <div className={classNames(s.menu, isActive ? s.active : '')} ref={menuRef}
                     onMouseLeave={() => setIsActive(false)}>
                    <div className={s.menuRow} onClick={(e) => read(e)}>
                        <div className={s.menuName}>{isUnreadMessage ? 'Отметить прочитанным' : 'Отметить непрочитанным'}</div>
                    </div>
                    {chat?.type !== 'notification' && (
                        <div className={s.menuRow} onClick={handleDelete}>
                            <div className={s.menuIcon}><DeleteChatIcon/></div>
                            <div className={s.menuName}>Удалить чат</div>
                        </div>
                    )}
                    {!chat?.isFixed && <div className={s.menuRow} onClick={handleClip}>
                        <div className={s.menuIcon}><ClipIcon width={'16'} height={'16'}/></div>
                        <div className={s.menuName}>Закрепить в списке чатов</div>
                    </div>}
                    {chat?.isFixed && <div className={s.menuRow} onClick={handleUnfix}>
                        <div className={s.menuIcon}><ClipIcon width={'16'} height={'16'}/></div>
                        <div className={s.menuName}>Открепить чат</div>
                    </div>}

                    {muted && <div className={s.menuRow} onClick={handleUnmute}>
                        <div className={s.menuName}>
                            Включить уведомления{externalChannels.length > 0 ? ' (общие)' : ''}
                        </div>
                    </div>}

                    {!muted && <div className={s.menuRow} onClick={handleMute}>
                        <div className={s.menuName}>
                            Отключить уведомления{externalChannels.length > 0 ? ' (общие)' : ''}
                        </div>
                    </div>}

                    {externalChannels.map((channel) => {
                        const ChannelIcon = EXTERNAL_CHANNEL_ICONS[channel]
                        return (
                            <div
                                className={s.menuRow}
                                onClick={handleExternalMute(channel, !externalMuted[channel])}
                                key={channel}
                            >
                                <div className={s.menuIcon}>
                                    {ChannelIcon && <ChannelIcon width={16} height={16} />}
                                </div>
                                <div className={s.menuName}>
                                    {externalMuted[channel] ? 'Включить' : 'Отключить'} уведомления — {EXTERNAL_CHANNEL_LABELS[channel]}
                                </div>
                            </div>
                        )
                    })}

                </div>
            </div>
        </>)
}

export default ChatView;
